import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { images } from "../i18n";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export const FacilitatorSection = ({ t }) => {
  const [active, setActive] = useState(t.facilitator.tabs[0].id);
  const tab = t.facilitator.tabs.find((x) => x.id === active) || t.facilitator.tabs[0];

  return (
    <section id="facilitateur" className="py-24 sm:py-32 lg:py-40 bg-surface grain relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div {...reveal} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brass mb-6">{t.facilitator.overline}</p>
            <h2 data-testid="chapter-01-title" className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05] text-ink">
              {t.facilitator.title}
            </h2>
          </div>
          <p className="lg:col-span-6 text-base sm:text-lg text-ink2 leading-relaxed">
            {t.facilitator.body}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div {...reveal} className="lg:col-span-5">
            <div data-testid="facilitator-methodology-tabs" className="flex flex-wrap gap-2 mb-8">
              {t.facilitator.tabs.map((tb) => (
                <button
                  key={tb.id}
                  onClick={() => setActive(tb.id)}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors duration-300 ${
                    active === tb.id
                      ? "bg-ink text-paper border-ink"
                      : "border-linestrong text-ink2 hover:border-ink hover:text-ink"
                  }`}
                >
                  {tb.label}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={active + tab.text}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                className="text-base sm:text-lg text-ink2 leading-relaxed min-h-[120px]"
              >
                {tab.text}
              </motion.p>
            </AnimatePresence>
            <div className="img-frame rounded-sm mt-8 aspect-[4/3]">
              <img src={images.event} alt={t.facilitator.imgAlt} className="w-full h-full object-cover" />
            </div>
          </motion.div>

          <div className="lg:col-span-7 flex flex-col">
            {t.facilitator.events.map((e, i) => (
              <motion.a
                key={e.id}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.06 }}
                data-testid={`event-card-${e.id}`}
                href={e.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start justify-between gap-6 py-7 border-b border-line first:border-t hover:bg-paper transition-colors duration-300 px-2 sm:px-4 -mx-2 sm:-mx-4"
              >
                <div>
                  <div className="flex items-baseline gap-4 flex-wrap">
                    <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink group-hover:text-brass transition-colors duration-300">
                      {e.title}
                    </h3>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brass">{e.period}</span>
                  </div>
                  <p className="text-sm sm:text-base font-medium text-ink mt-2">{e.role}</p>
                  <p className="text-sm text-ink2 leading-relaxed mt-1.5 max-w-xl">{e.desc}</p>
                </div>
                <ArrowUpRight size={18} className="shrink-0 mt-2 text-ink3 group-hover:text-ink transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
